/**
 * Timezone Utility Functions
 * Handles timezone conversions for displaying booking times in user's local timezone
 */

import { format, parseISO } from 'date-fns';
import { toZonedTime, fromZonedTime, formatInTimeZone } from 'date-fns-tz';

// Default timezone (Eastern Time - Toronto)
export const DEFAULT_TIMEZONE = 'America/Toronto';

// Timezone display names
export const TIMEZONE_DISPLAY_NAMES = {
  'America/St_Johns': 'Newfoundland Time (NST/NDT)',
  'America/Halifax': 'Atlantic Time (AST/ADT)',
  'America/Moncton': 'Atlantic Time (AST/ADT)',
  'America/Toronto': 'Eastern Time (EST/EDT)',
  'America/Montreal': 'Eastern Time (EST/EDT)',
  'America/Winnipeg': 'Central Time (CST/CDT)',
  'America/Regina': 'Central Standard Time (CST)',
  'America/Edmonton': 'Mountain Time (MST/MDT)',
  'America/Vancouver': 'Pacific Time (PST/PDT)',
  'America/Whitehorse': 'Mountain Standard Time (MST)',
  'America/Yellowknife': 'Mountain Time (MST/MDT)',
  'America/Iqaluit': 'Eastern Time (EST/EDT)',
  'America/Rankin_Inlet': 'Central Time (CST/CDT)',
  'America/Cambridge_Bay': 'Mountain Time (MST/MDT)',
  'America/Goose_Bay': 'Atlantic Time (AST/ADT)',
  'America/Creston': 'Mountain Standard Time (MST)',
  'America/Dawson_Creek': 'Mountain Standard Time (MST)',
  'America/Fort_Nelson': 'Mountain Standard Time (MST)',
  'America/Thunder_Bay': 'Eastern Time (EST/EDT)',
  'America/Rainy_River': 'Central Time (CST/CDT)',
};

/**
 * Get timezone abbreviation (e.g., "EST", "PST")
 * @param {string} timezone - IANA timezone identifier
 * @param {Date} date - Optional date for DST consideration
 * @returns {string} Timezone abbreviation
 */
export const getTimezoneAbbr = (timezone, date = new Date()) => {
  try {
    return formatInTimeZone(date, timezone, 'zzz');
  } catch {
    return '';
  }
};

/**
 * Get UTC offset string (e.g., "UTC-5", "UTC+0", "UTC-8")
 * @param {string} timezone - IANA timezone identifier
 * @param {Date} date - Optional date for DST consideration
 * @returns {string} UTC offset string
 */
export const getUTCOffset = (timezone, date = new Date()) => {
  try {
    if (!timezone) return '';
    
    const zonedDate = toZonedTime(date, timezone);
    const offsetMinutes = zonedDate.getTimezoneOffset ? -zonedDate.getTimezoneOffset() : 0;
    
    // Calculate offset from the timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'longOffset'
    });
    
    const parts = formatter.formatToParts(date);
    const offsetPart = parts.find(p => p.type === 'timeZoneName');
    
    if (offsetPart) {
      // Extract offset like "GMT-05:00" and convert to "UTC-5"
      const match = offsetPart.value.match(/GMT([+-])(\d{2}):(\d{2})/);
      if (match) {
        const sign = match[1];
        const hours = parseInt(match[2], 10);
        const minutes = parseInt(match[3], 10);
        
        if (hours === 0 && minutes === 0) {
          return 'UTC±0';
        }
        
        let result = `UTC${sign}${hours}`;
        if (minutes > 0) {
          result += `:${minutes.toString().padStart(2, '0')}`;
        }
        return result;
      }
    }
    
    return '';
  } catch (error) {
    console.error('Error getting UTC offset:', error);
    return '';
  }
};

/**
 * Convert a time string from one timezone to another
 * @param {string} timeStr - Time string in HH:mm format (e.g., "14:00")
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {string} fromTimezone - Source timezone (IANA format)
 * @param {string} toTimezone - Target timezone (IANA format)
 * @returns {string} Converted time in HH:mm format
 */
export const convertTime = (timeStr, dateStr, fromTimezone, toTimezone) => {
  try {
    if (!timeStr || !dateStr || !fromTimezone || !toTimezone) {
      return timeStr;
    }

    // Create a date object in the source timezone
    const dateTimeStr = `${dateStr}T${timeStr}:00`;
    const sourceDate = fromZonedTime(dateTimeStr, fromTimezone);
    
    // Convert to target timezone
    const targetDate = toZonedTime(sourceDate, toTimezone);
    
    return format(targetDate, 'HH:mm');
  } catch (error) {
    console.error('Error converting time:', error);
    return timeStr;
  }
};

/**
 * Format a booking time for display in user's timezone
 * @param {string} timeStr - Time string in HH:mm format
 * @param {string} dateStr - Date string in YYYY-MM-DD format  
 * @param {string} trainerTimezone - Trainer's timezone
 * @param {string} userTimezone - User's timezone
 * @param {boolean} showTimezoneAbbr - Whether to show timezone abbreviation
 * @returns {string} Formatted time string
 */
export const formatBookingTimeForUser = (timeStr, dateStr, trainerTimezone, userTimezone, showTimezoneAbbr = false) => {
  try {
    if (!userTimezone || !trainerTimezone || userTimezone === trainerTimezone) {
      return showTimezoneAbbr ? `${timeStr} ${getTimezoneAbbr(trainerTimezone)}` : timeStr;
    }

    const convertedTime = convertTime(timeStr, dateStr, trainerTimezone, userTimezone);
    
    if (showTimezoneAbbr) {
      return `${convertedTime} ${getTimezoneAbbr(userTimezone)}`;
    }
    
    return convertedTime;
  } catch {
    return timeStr;
  }
};

/**
 * Format a date with timezone consideration
 * @param {string|Date} date - Date to format
 * @param {string} timezone - Target timezone
 * @param {string} formatStr - date-fns format string
 * @returns {string} Formatted date string
 */
export const formatDateInTimezone = (date, timezone, formatStr = 'MMMM d, yyyy') => {
  try {
    if (!date) return '';
    
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    
    if (!timezone) {
      return format(dateObj, formatStr);
    }
    
    return formatInTimeZone(dateObj, timezone, formatStr);
  } catch (error) {
    console.error('Error formatting date:', error);
    return typeof date === 'string' ? date : '';
  }
};

/**
 * Format a datetime with timezone consideration
 * @param {string|Date} dateTime - DateTime to format
 * @param {string} timezone - Target timezone
 * @param {string} formatStr - date-fns format string
 * @returns {string} Formatted datetime string
 */
export const formatDateTimeInTimezone = (dateTime, timezone, formatStr = 'MMM d, yyyy h:mm a') => {
  try {
    if (!dateTime) return '';
    
    const dateObj = typeof dateTime === 'string' ? parseISO(dateTime) : dateTime;
    
    if (!timezone) {
      return format(dateObj, formatStr);
    }
    
    return formatInTimeZone(dateObj, timezone, formatStr);
  } catch (error) {
    console.error('Error formatting datetime:', error);
    return typeof dateTime === 'string' ? dateTime : '';
  }
};

/**
 * Get the time difference description between two timezones
 * @param {string} fromTimezone - Source timezone
 * @param {string} toTimezone - Target timezone
 * @returns {string} Description of time difference (e.g., "3 hours ahead", "2 hours behind")
 */
export const getTimezoneDifference = (fromTimezone, toTimezone) => {
  try {
    if (!fromTimezone || !toTimezone || fromTimezone === toTimezone) {
      return 'Same timezone';
    }

    const now = new Date();
    const fromTime = toZonedTime(now, fromTimezone);
    const toTime = toZonedTime(now, toTimezone);
    
    const diffHours = (toTime.getTime() - fromTime.getTime()) / (1000 * 60 * 60);
    
    if (diffHours === 0) {
      return 'Same timezone';
    } else if (diffHours > 0) {
      return `${Math.abs(diffHours)} hour${Math.abs(diffHours) !== 1 ? 's' : ''} ahead`;
    } else {
      return `${Math.abs(diffHours)} hour${Math.abs(diffHours) !== 1 ? 's' : ''} behind`;
    }
  } catch {
    return '';
  }
};

/**
 * Check if two timezones are different
 * @param {string} tz1 - First timezone
 * @param {string} tz2 - Second timezone
 * @returns {boolean} True if timezones are different
 */
export const areTimezonesDifferent = (tz1, tz2) => {
  if (!tz1 || !tz2) return false;
  return tz1 !== tz2;
};

/**
 * Format booking time with original and converted times
 * Shows both trainer's time and user's local time if different
 * @param {Object} booking - Booking object with time, date properties
 * @param {string} trainerTimezone - Trainer's timezone
 * @param {string} userTimezone - User's timezone
 * @returns {Object} Object with originalTime, convertedTime, showBoth
 */
export const getBookingTimeDisplay = (booking, trainerTimezone, userTimezone) => {
  const originalTime = booking.time;
  const bookingDate = booking.date;
  
  // If same timezone or missing data, just return original
  if (!trainerTimezone || !userTimezone || trainerTimezone === userTimezone) {
    return {
      originalTime,
      convertedTime: originalTime,
      showBoth: false,
      userTimezone: userTimezone || trainerTimezone,
      trainerTimezone: trainerTimezone
    };
  }
  
  const convertedTime = convertTime(originalTime, bookingDate, trainerTimezone, userTimezone);
  
  return {
    originalTime,
    convertedTime,
    showBoth: originalTime !== convertedTime,
    userTimezone,
    trainerTimezone
  };
};

export default {
  convertTime,
  formatBookingTimeForUser,
  formatDateInTimezone,
  formatDateTimeInTimezone,
  getTimezoneDifference,
  areTimezonesDifferent,
  getBookingTimeDisplay,
  getTimezoneAbbr,
  getUTCOffset,
  DEFAULT_TIMEZONE,
  TIMEZONE_DISPLAY_NAMES,
};

/**
 * Get user's effective timezone
 * Priority: 1. User profile timezone (if logged in) 2. Browser timezone
 * @param {Object} user - User object from AuthContext
 * @returns {string} IANA timezone identifier
 */
export const getUserEffectiveTimezone = (user) => {
  // If user is logged in and has a timezone set in their profile, use it
  if (user && user.timezone) {
    return user.timezone;
  }
  
  // Fallback to browser timezone for guests or users without timezone set
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return DEFAULT_TIMEZONE;
  }
};

/**
 * Format group class datetime in user's effective timezone
 * @param {string} scheduledDate - Date in YYYY-MM-DD format
 * @param {string} startTime - Time in HH:mm format
 * @param {string} trainerTimezone - Trainer's timezone (IANA format)
 * @param {string} utcTime - UTC time string (ISO format)
 * @param {Object} user - User object from AuthContext (optional)
 * @returns {string} Formatted datetime string with timezone abbreviation
 */
export const formatGroupClassDateTime = (scheduledDate, startTime, trainerTimezone, utcTime, user = null) => {
  try {
    // Get user's effective timezone (profile setting or browser)
    const userTimezone = getUserEffectiveTimezone(user);
    
    // If we have UTC time, use it directly for accurate conversion
    if (utcTime) {
      const utcDate = parseISO(utcTime);
      const formattedDate = formatInTimeZone(utcDate, userTimezone, 'MMM d');
      const formattedTime = formatInTimeZone(utcDate, userTimezone, 'HH:mm');
      const tzAbbr = formatInTimeZone(utcDate, userTimezone, 'zzz');
      return `${formattedDate} ${formattedTime} ${tzAbbr}`;
    }
    
    // Fallback: Use trainer timezone if no UTC time available
    const tz = trainerTimezone || DEFAULT_TIMEZONE;
    const dateTimeStr = `${scheduledDate}T${startTime}:00`;
    const trainerDate = fromZonedTime(dateTimeStr, tz);
    
    // Format in user's timezone
    const formattedDate = formatInTimeZone(trainerDate, userTimezone, 'MMM d');
    const formattedTime = formatInTimeZone(trainerDate, userTimezone, 'HH:mm');
    const tzAbbr = formatInTimeZone(trainerDate, userTimezone, 'zzz');
    return `${formattedDate} ${formattedTime} ${tzAbbr}`;
  } catch (error) {
    console.error('Error formatting group class datetime:', error);
    return `${scheduledDate} ${startTime}`;
  }
};
